import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useTokens,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { getContrastTextColor } from '@vibesites/theme-rise/utils/luminance';

// ============================================================================
// Why this fork exists
// ----------------------------------------------------------------------------
// This HeroSolidBG mirrors `@vibesites/theme-rise`'s HeroSolidBG verbatim with
// one intentional Weidner-only deviation: the inner flex container's top
// padding is bumped on mobile and tablet so the title gets more breathing
// room above it on those viewports.
//
//   Parent (rise base): `pt-6 md:pt-4 lg:pt-16`
//     - mobile: 1.5rem, md/tablet: 1rem, lg/desktop: 4rem
//   Fork (Weidner):     `pt-12 md:pt-12 lg:pt-16`
//     - mobile: 3rem,   md/tablet: 3rem, lg/desktop: 4rem (unchanged)
//
// Weidner properties wanted the title to sit lower on tablet and mobile —
// the parent's mobile/tablet values left it visually crowded against the
// transparent nav overlay (and tablet was actually tighter than mobile).
// Desktop spacing was already comfortable and is left untouched.
//
// Maintenance checklist when re-syncing with the rise parent:
//   1. Diff this file against
//      `vendor/vibesites/theme-rise/components/HeroSolidBG.tsx`.
//   2. Pull any non-padding changes (schema additions, CTA tweaks, the inline
//      <style> block, accessibility fixes) into this fork.
//   3. Keep the `pt-12 md:pt-12 lg:pt-16` classes on the inner flex container.
//   4. Run `npm run lint:schema`.
// ============================================================================

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  blurbField?: string;
  defaultText?: string;
  ctaLabel?: string;
  ctaUrl?: string;
}

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  blurbField,
  defaultText,
  ctaLabel = 'View Floor Plans',
  ctaUrl = '/floor-plans',
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const blurbPath = `${prefix}.blurb`;
  const ctaPath = `${prefix}.cta_link`;

  // The default URL tracks the default CTA label so the link target matches
  // the verb ("View Floor Plans" → /floor-plans). Each page in pages.json
  // is expected to pass `ctaLabel` and `ctaUrl` together when overriding;
  // property_apply_now_url / property_website_url remain available to
  // editors via suggestedFields.
  const ctaLinkDef: SlotGroupDefinition = {
    label: 'CTA Link',
    fields: {
      url: {
        label: 'URL',
        type: 'url',
        defaultValue: ctaUrl,
        suggestedFields: ['property_apply_now_url', 'property_website_url'],
      },
      label: {
        label: 'Link Text',
        type: 'text',
        defaultValue: ctaLabel,
      },
      _target: {
        label: 'Target',
        type: 'text',
        defaultValue: '_self',
      },
    },
  };

  const cta = useSlotGroup(ctaPath, ctaLinkDef);

  // Pick a readable text/border color for the CTA on top of the primary
  // background. Mirrors the Footer's `getContrastTextColor` so the CTA stays
  // in lockstep with `--vibe-color-text-on-primary` (which the tokens runtime
  // sets via the same threshold). The earlier `pickHighestContrast` between
  // `textOnPrimary` and `textOnSecondary` was technically more "WCAG-optimal"
  // but produced black-on-medium-tone primaries (gold, tan, dusty blue) where
  // the desired visual answer — and the runtime's own text-on-primary token —
  // is white. The shared helper applies the documented 0.55 luminance
  // threshold that favors white on those medium tones.
  const tokens = useTokens();
  const onSolidColor = useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return null;
    return getContrastTextColor(primary);
  }, [tokens]);

  return (
    <section
      className="bg-primary text-primary-foreground pb-14 md:pb-20"
      style={{
        paddingTop: 'calc(var(--offer-banner-height, 0px) + 5rem)',
        transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        // Drives `.hero-solid-cta-btn` below. The CSS rules fall back to white
        // when this var is absent (e.g. tokens haven't hydrated yet) so the
        // visual is identical to the previous hardcoded behavior.
        ...(onSolidColor ? { ['--rise-hero-solid-text' as string]: onSolidColor } : {}),
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Weidner deviation: mobile/tablet top padding bumped from the rise
            base (`pt-6 md:pt-4`) to `pt-12 md:pt-12` so the title gets more
            breathing room above it on those viewports. Desktop (`lg:pt-16`)
            is unchanged. */}
        <div className="flex flex-col gap-6 items-center pt-12 md:pt-12 lg:pt-16">

          {/* Page Title */}
          <div className="min-w-0 w-full">
            <RichTextContent
              path={titlePath}
              defaultValue={`<p>${title}</p>`}
              as="h1"
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-wider leading-tight break-words text-center"
            />
          </div>

          {/* Blurb + CTA */}
          <RichTextContent
            path={blurbPath}
            defaultField={blurbField}
            defaultValue={defaultText || ''}
            as="div"
            className="font-body text-base md:text-lg leading-relaxed opacity-90 [&_a]:underline text-center"
          />
          <div className="text-center">
            <EditableSlotGroup groupPath={ctaPath} definition={ctaLinkDef}>
              <a
                href={cta.url || '#'}
                target={cta._target || '_self'}
                className="hero-solid-cta-btn group/cta relative inline-flex items-center gap-2 px-8 py-3 font-heading text-sm uppercase tracking-wider overflow-hidden transition-all duration-300"
              >
                <span className="relative z-10">{cta.label || ctaLabel}</span>
              </a>
            </EditableSlotGroup>
          </div>

        </div>
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.cta_link"
        definition={ctaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        /* Mirrors the .nav-cta-btn hover behavior from Header, adapted for
           the solid-primary hero background. Border / text / hover fill all
           track --rise-hero-solid-text, which the section sets via a
           luminance check against the primary brand color (textOnPrimary vs
           textOnSecondary, whichever has higher WCAG contrast). The white
           fallbacks preserve the previous visual when the var is absent. */
        .hero-solid-cta-btn {
          background: transparent;
          border: 1.5px solid color-mix(in srgb, var(--rise-hero-solid-text, #fff) 75%, transparent);
          border-radius: 2px;
          color: var(--rise-hero-solid-text, #fff);
          box-shadow: none;
        }
        .hero-solid-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: color-mix(in srgb, var(--rise-hero-solid-text, #fff) 18%, transparent);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .hero-solid-cta-btn:hover {
          border-color: var(--rise-hero-solid-text, #fff);
          color: var(--rise-hero-solid-text, #fff);
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.15),
            0 4px 16px color-mix(in srgb, var(--rise-hero-solid-text, #fff) 18%, transparent);
        }
        .hero-solid-cta-btn:hover::before {
          opacity: 1;
        }
        .hero-solid-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Floor Plans',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
